import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Share2, Power, PowerOff, Trash2, Mail, Copy, Check, Loader2, Plus, Navigation, Send, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TripShare({ token }) {
  const { t } = useTranslation();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTrip, setNewTrip] = useState({ trip_name: '', destination: '', traveler_name: '', traveler_email: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(null);
  const [copied, setCopied] = useState(null);
  const [trackingActive, setTrackingActive] = useState(null);
  const [resending, setResending] = useState(null);
  const watchRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTrips = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/trip/my-trips`, { headers });
      setTrips(res.data.trips || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);

  // Location tracking
  const startTracking = useCallback((shareId) => {
    if (!navigator.geolocation) return alert('Geolocalizzazione non supportata');
    
    setTrackingActive(shareId);
    
    // Send initial position
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await axios.post(`${API}/trip/${shareId}/update-location`, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          }, { headers });
        } catch (e) { console.error(e); }
      },
      (err) => console.error('Geo error:', err),
      { enableHighAccuracy: true }
    );

    // Watch position
    watchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        try {
          await axios.post(`${API}/trip/${shareId}/update-location`, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          }, { headers });
        } catch (e) { console.error(e); }
      },
      (err) => console.error('Watch error:', err),
      { enableHighAccuracy: true, maximumAge: 60000, timeout: 30000 }
    );
  }, [token]);

  const stopTracking = useCallback(() => {
    if (watchRef.current !== null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    setTrackingActive(null);
  }, []);

  useEffect(() => { return () => stopTracking(); }, [stopTracking]);

  const createTrip = async () => {
    if (!newTrip.trip_name) return;
    setCreating(true);
    try {
      await axios.post(`${API}/trip/create`, newTrip, { headers });
      setNewTrip({ trip_name: '', destination: '', traveler_name: '', traveler_email: '' });
      setShowCreate(false);
      fetchTrips();
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const toggleTrip = async (shareId) => {
    try {
      const res = await axios.patch(`${API}/trip/${shareId}/toggle`, {}, { headers });
      if (!res.data.is_active) stopTracking();
      fetchTrips();
    } catch (e) { console.error(e); }
  };

  const deleteTrip = async (shareId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo viaggio?')) return;
    try {
      await axios.delete(`${API}/trip/${shareId}`, { headers });
      if (trackingActive === shareId) stopTracking();
      fetchTrips();
    } catch (e) { console.error(e); }
  };

  const inviteRecipient = async (shareId) => {
    if (!inviteEmail.trim()) return;
    setInviting(shareId);
    try {
      await axios.post(`${API}/trip/${shareId}/invite`, { emails: [inviteEmail.trim()] }, { headers });
      setInviteEmail('');
      fetchTrips();
    } catch (e) { console.error(e); }
    setInviting(null);
  };

  const copyLink = (shareId) => {
    const url = `https://esim4you.com?page=track&id=${shareId}`;
    navigator.clipboard.writeText(url);
    setCopied(shareId);
    setTimeout(() => setCopied(null), 2000);
  };

  const resendActivation = async (shareId, email) => {
    setResending(shareId);
    try {
      await axios.post(`${API}/trip/${shareId}/resend-activation`, { email: email || '' }, { headers });
    } catch (e) { console.error(e); }
    setResending(null);
  };

  const getTrackingUrl = (shareId) => `https://esim4you.com?page=track&id=${shareId}`;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal-400" /></div>;

  return (
    <div className="space-y-6" data-testid="trip-share-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Navigation className="w-5 h-5 text-teal-400" />
            Condividi il mio viaggio
          </h2>
          <p className="text-slate-400 text-sm mt-1">Condividi la tua posizione in tempo reale con familiari e amici</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 text-teal-300 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
          data-testid="create-trip-btn">
          <Plus className="w-4 h-4" /> Nuovo viaggio
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-white/5 border border-teal-500/20 rounded-xl p-5 space-y-4" data-testid="create-trip-form">
          <input value={newTrip.trip_name} onChange={e => setNewTrip({...newTrip, trip_name: e.target.value})}
            placeholder="Nome del viaggio (es. Vacanza a Tokyo)"
            className="w-full bg-white/5 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" />
          <div className="grid grid-cols-2 gap-3">
            <input value={newTrip.destination} onChange={e => setNewTrip({...newTrip, destination: e.target.value})}
              placeholder="Destinazione (es. Giappone)"
              className="bg-white/5 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" />
            <input value={newTrip.traveler_name} onChange={e => setNewTrip({...newTrip, traveler_name: e.target.value})}
              placeholder="Nome viaggiatore (es. Elena)"
              className="bg-white/5 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" />
          </div>
          <div className="relative">
            <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
            <input value={newTrip.traveler_email} onChange={e => setNewTrip({...newTrip, traveler_email: e.target.value})}
              placeholder="Email del viaggiatore (ricevera link GPS)"
              type="email"
              className="w-full bg-white/5 border border-teal-500/30 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none"
              data-testid="traveler-email-input" />
          </div>
          <p className="text-slate-500 text-xs -mt-2 ml-1">Il viaggiatore ricevera un'email con il link per attivare il GPS sul telefono</p>
          <div className="flex gap-3">
            <button onClick={createTrip} disabled={creating || !newTrip.trip_name}
              className="flex-1 bg-teal-600 hover:bg-teal-500 text-white py-3 rounded-lg font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-create-trip-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Crea viaggio
            </button>
            <button onClick={() => setShowCreate(false)} className="px-6 bg-white/5 hover:bg-white/10 text-slate-400 py-3 rounded-lg transition-all">
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Trip list */}
      {trips.length === 0 && !showCreate ? (
        <div className="text-center py-12 bg-white/5 rounded-xl border border-slate-800">
          <Navigation className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nessun viaggio condiviso</p>
          <p className="text-slate-600 text-sm mt-1">Crea un nuovo viaggio per condividere la tua posizione</p>
        </div>
      ) : (
        <div className="space-y-4">
          {trips.map(trip => (
            <div key={trip.share_id} className={`rounded-xl border p-5 transition-all ${trip.is_active ? 'bg-teal-500/5 border-teal-500/20' : 'bg-white/5 border-slate-800'}`}
              data-testid={`trip-card-${trip.share_id}`}>
              
              {/* Trip header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                    <MapPin className={`w-4 h-4 ${trip.is_active ? 'text-teal-400' : 'text-slate-600'}`} />
                    {trip.trip_name}
                  </h3>
                  {trip.destination && <p className="text-slate-400 text-sm">{trip.destination}</p>}
                  <p className="text-slate-600 text-xs mt-1">
                    {trip.locations?.length || 0} posizioni registrate
                    {trip.is_active && <span className="text-teal-400 ml-2">Attivo</span>}
                    {!trip.is_active && <span className="text-slate-600 ml-2">Disattivato</span>}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggleTrip(trip.share_id)} title={trip.is_active ? "Disattiva" : "Attiva"}
                    className={`p-2 rounded-lg transition-all ${trip.is_active ? 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30' : 'bg-white/5 text-slate-500 hover:bg-white/10'}`}
                    data-testid={`toggle-trip-${trip.share_id}`}>
                    {trip.is_active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => deleteTrip(trip.share_id)} title="Elimina"
                    className="p-2 rounded-lg bg-white/5 text-rose-400 hover:bg-rose-500/10 transition-all"
                    data-testid={`delete-trip-${trip.share_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Traveler email + resend activation */}
              {trip.traveler_email && (
                <div className="bg-teal-500/5 border border-teal-500/10 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-xs mb-0.5">Email viaggiatore (link GPS inviato a):</p>
                      <p className="text-teal-400 text-sm font-medium">{trip.traveler_email}</p>
                    </div>
                    <button onClick={() => resendActivation(trip.share_id, trip.traveler_email)}
                      disabled={resending === trip.share_id}
                      className="bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 text-teal-300 px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1 disabled:opacity-50"
                      data-testid={`resend-activation-${trip.share_id}`}>
                      {resending === trip.share_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Reinvia link
                    </button>
                  </div>
                </div>
              )}

              {/* Tracking button */}
              {trip.is_active && (
                <div className="mb-4">
                  {trackingActive === trip.share_id ? (
                    <button onClick={stopTracking}
                      className="w-full bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-300 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                      data-testid="stop-tracking-btn">
                      <div className="w-2 h-2 bg-rose-400 rounded-full animate-pulse" />
                      Interrompi tracking GPS
                    </button>
                  ) : (
                    <button onClick={() => startTracking(trip.share_id)}
                      className="w-full bg-teal-600 hover:bg-teal-500 text-white py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                      data-testid="start-tracking-btn">
                      <Navigation className="w-4 h-4" />
                      Attiva tracking GPS
                    </button>
                  )}
                </div>
              )}

              {/* Share link */}
              <div className="bg-black/20 rounded-lg p-3 mb-4">
                <p className="text-slate-500 text-xs mb-2">Link di condivisione:</p>
                <div className="flex gap-2">
                  <input readOnly value={getTrackingUrl(trip.share_id)}
                    className="flex-1 bg-transparent text-teal-400 text-sm truncate border-none outline-none" />
                  <button onClick={() => copyLink(trip.share_id)}
                    className="bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded-lg text-sm transition-all flex items-center gap-1"
                    data-testid={`copy-link-${trip.share_id}`}>
                    {copied === trip.share_id ? <Check className="w-3 h-3 text-teal-400" /> : <Copy className="w-3 h-3" />}
                    {copied === trip.share_id ? 'Copiato!' : 'Copia'}
                  </button>
                </div>
              </div>

              {/* Invite section */}
              <div>
                <p className="text-slate-500 text-xs mb-2">Invita familiari o amici via email:</p>
                <div className="flex gap-2">
                  <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    placeholder="Email familiare/amico"
                    onKeyDown={e => e.key === 'Enter' && inviteRecipient(trip.share_id)}
                    className="flex-1 bg-white/5 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-teal-500 focus:outline-none" />
                  <button onClick={() => inviteRecipient(trip.share_id)} disabled={inviting === trip.share_id}
                    className="bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 text-teal-300 px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-1 disabled:opacity-50"
                    data-testid={`invite-btn-${trip.share_id}`}>
                    {inviting === trip.share_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                    Invita
                  </button>
                </div>
                {trip.recipients?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {trip.recipients.map(r => (
                      <span key={r} className="text-xs bg-teal-500/10 text-teal-400 px-2 py-0.5 rounded-full">{r}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
